import {ChangeDetectionStrategy, ChangeDetectorRef, Component, DestroyRef, inject, OnInit} from '@angular/core';
import {takeUntilDestroyed, toSignal} from '@angular/core/rxjs-interop';
import {NavigationEnd, Router} from '@angular/router';
import {of} from 'rxjs';
import {catchError, filter, map} from 'rxjs/operators';
import { StorageUrlService } from 'src/app/services/storage-url.service';
import {AuthService} from "../../services/auth.service";
import {IUser} from "../../interfaces/user.interface";
import {environment} from '../../../environments/environment';
import {PermissionService} from '../../services/permission.service';
import {SupportChatService} from '../../services/support-chat.service';
import {CoCoachService} from '../../services/co-coach.service';
import {TaggingService} from '../../services/tagging.service';
import {EModulePermissions, Permission} from '../../enums/permission.enum';
import {LiveService} from '../../pages/live/services/live.service';

/** De knoppen in de balk die van rechten afhangen. */
type NavKey = 'live' | 'schedule' | 'video' | 'playlist' | 'upload' | 'match' | 'players' | 'search';

@Component({
  selector: 'app-header',
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class HeaderComponent implements OnInit {
  private storageUrlService = inject(StorageUrlService);
  private router = inject(Router);
  private cdr = inject(ChangeDetectorRef);
  private destroyRef = inject(DestroyRef);
  authService = inject(AuthService);
  permissionService = inject(PermissionService);
  supportChat = inject(SupportChatService);
  coCoach = inject(CoCoachService);
  private tagging = inject(TaggingService);
  private liveService = inject(LiveService);

  public get storageUrl(): string {
    return this.storageUrlService.base;
  }
  public readonly logo = environment.logo;

  public user: IUser = this.authService.user;

  /**
   * Een taggingteam (Slick) werkt alleen aan opdrachten: tagger en
   * beheerder zien dan alleen Tagging in de balk, geen wedstrijden,
   * trainingen, spelers of uploaden (eigenaar 30-09-2026: "enkel focus op
   * tagging").
   */
  public taggingTeam = toSignal(this.tagging.isTaggingTeam(), { initialValue: this.tagging.isTagger() });

  /**
   * Speelt een team van deze club in een league? Alleen vanuit de eigen
   * club: in de league-omgeving zelf toont Wedstrijden de league al.
   */
  public heeftLeague = toSignal(
    this.user?.home_club
      ? of(false)
      : this.authService.omgevingen().pipe(
          map((omgevingen) => (omgevingen ?? []).some((o) => o.kind === 'league')),
          catchError(() => of(false)),
        ),
    { initialValue: false },
  );

  private readonly volledig: Record<NavKey, boolean> = {
    live: this.permissionService.hasPermission(Permission.LIVE_SHOW) && environment.permissions.liveShow,
    schedule: this.permissionService.hasPermission(Permission.RECORDERS_SCHEDULE_SHOW) && environment.permissions.recordersScheduleShow && this.permissionService.checkModulePermission(EModulePermissions.SCHEDULING),
    video: this.permissionService.hasPermission(Permission.VIDEO_SHOW) && environment.permissions.videoShow  && this.permissionService.checkModulePermission(EModulePermissions.VIDEO_STORAGE),
    playlist: this.permissionService.hasPermission(Permission.PLAYLISTS_SHOW) && environment.permissions.playlistsShow && this.permissionService.checkModulePermission(EModulePermissions.VIDEO_STORAGE),
    upload: this.permissionService.hasPermission(Permission.UPLOAD_REQUEST) && environment.permissions.uploadRequest && this.permissionService.checkModulePermission(EModulePermissions.VIDEO_STORAGE),
    match: this.permissionService.hasPermission(Permission.MATCH_SHOW) && environment.permissions.matchShow,
    players: this.permissionService.hasPermission(Permission.PLAYERS_SHOW) && environment.permissions.playersShow,
    // Zoeken speelt momenten uit wedstrijdvideo's af: wedstrijden én opslag.
    search: this.permissionService.hasPermission(Permission.MATCH_SHOW) && environment.permissions.matchShow && this.permissionService.checkModulePermission(EModulePermissions.VIDEO_STORAGE),
  };

  /**
   * Live alleen als er iets live te zien is: een camera van een studio of
   * een producer. Zonder aangesloten camera's (een league-omgeving) was Live
   * een lege pagina (eigenaar 16-10-2026: "Live mag weg, want er zijn geen live
   * cameras aangesloten"). Lukt het ophalen niet, dan blijft Live staan.
   */
  public heeftCameras = toSignal(
    this.volledig.live && this.volledig.schedule
      ? this.liveService.list().pipe(
          map((items) => this.liveService.groepeer(items ?? []).some((groep) => groep.cameras.length > 0)),
          catchError(() => of(true)),
        )
      : of(false),
    { initialValue: false },
  );

  private readonly geen: Record<NavKey, boolean> = {
    live: false, schedule: false, video: false, playlist: false, upload: false, match: false, players: false, search: false,
  };

  /** Een spelersaccount: "Mijn profiel" vooraan in het menu. */
  public readonly speler = this.permissionService.isSpeler();

  public get permissions(): Record<NavKey, boolean> {
    return this.taggingTeam() ? this.geen : this.volledig;
  }

  ngOnInit(): void {
    this.router.events.pipe(
      filter((e): e is NavigationEnd => e instanceof NavigationEnd),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe(() => this.cdr.markForCheck());
  }

  /** Pad zonder query, voor actieve menustand. */
  private pad(): string {
    return this.router.url.split('?')[0];
  }

  /** Soort in de adresregel van /videos (match of training). */
  private soort(): string | null {
    return this.router.parseUrl(this.router.url).queryParams['soort'] ?? null;
  }

  /**
   * Wedstrijden: de tabel (/matches…) en alles onder /videos behalve de
   * trainingen -- ook "Niet ingedeeld", "In behandeling" en een geopende
   * video, want Video's staat niet meer in de balk.
   */
  public isWedstrijdenActief(): boolean {
    const pad = this.pad();
    if (this.isLeagueActief()) return false;
    return pad.startsWith('/matches') || (pad.startsWith('/videos') && !this.isTrainingenActief());
  }

  public isLeagueActief(): boolean {
    return this.pad().startsWith('/matches/league');
  }

  /** Trainingen = /videos met soort=training. */
  public isTrainingenActief(): boolean {
    return this.pad() === '/videos' && this.soort() === 'training';
  }
}
